import { gql, skipToken, useQuery, useSuspenseQuery } from '@apollo/client'
import { useMemo } from 'react'
import { locale } from '../../common/region/locale'
import { useCurrentUserId } from '../../common/user/currentUser'
import { getApolloClient, preloadQuery } from '../apollo/apolloClientSingleton'
import {
  calculateAttributeScore,
  calculateAttributeScoreFlattened,
  getAttributeScoreWeightedAverage
} from './calculateAttributeScore'

const PERSONALISATION_VALUES_QUERY = gql`
  query getUserPersonalisationValues($locale: String!) {
    getUserPersonalisationValues(locale: $locale) {
      _id
      categoryScores {
        categoryObjectId
        nameLocalised
        baseCategory
        score
      }
      sizeScores {
        size
        score
      }
      demographyScores {
        demography
        score
      }
      brandScores {
        brandObjectId
        name
        score
        styles
        pricePoint
      }
    }
  }
`

export const addAttributeScores = (data) => {
  const pricePointScores = calculateAttributeScore(data?.brandScores, 'pricePoint')
  const styleScores = calculateAttributeScoreFlattened(data?.brandScores, 'styles')
  const baseCategoryScores = calculateAttributeScore(data?.categoryScores, 'baseCategory')

  return {
    ...data,
    baseCategoryScores,
    styleScores,
    pricePointScores,
    pricePointAverage: getAttributeScoreWeightedAverage(pricePointScores, 'pricePoint')
  }
}

export const usePersonalisationValues = () => {
  const userId = useCurrentUserId()
  const { data, loading, error } = useQuery(PERSONALISATION_VALUES_QUERY, {
    skip: !userId,
    variables: { locale }
  })
  const extendedScores = useMemo(
    () => addAttributeScores(data?.getUserPersonalisationValues),
    [data]
  )
  return { data: extendedScores, loading, error }
}

export const useSuspensePersonalisationValues = ({ skip } = {}) => {
  const userId = useCurrentUserId()
  const { data } = useSuspenseQuery(
    PERSONALISATION_VALUES_QUERY,
    userId && !skip ? { variables: { locale } } : skipToken
  )
  const extendedScores = useMemo(
    () => addAttributeScores(data?.getUserPersonalisationValues),
    [data]
  )
  return { data: extendedScores }
}

export const preloadPersonalisationValues = () =>
  preloadQuery(PERSONALISATION_VALUES_QUERY, {
    variables: { locale }
  })

export const getCachedPersonalisationValues = () => {
  const personalisationQuery = getApolloClient().readQuery({
    query: PERSONALISATION_VALUES_QUERY,
    variables: { locale }
  })

  return personalisationQuery
    ? addAttributeScores(personalisationQuery.getUserPersonalisationValues)
    : undefined
}
